import { getActivePinia } from 'pinia'
import type { RouteLocationRaw } from 'vue-router'

import { getConfig } from '../config'
import { useAuthStore } from '../stores/auth'
import type { Session } from '../stores/session'

import { ROUTE_LOGIN, ROUTE_PROFILE } from './names'

/** The AI mode page (`src/modules/ai-mode/routes.ts`, docs/ai-mode). */
export const AI_MODE_PATH = '/ai'

/**
 * The navbar's Classic / AI switch is hidden for now (owner, 2026-10-27: the assistant pill opens the AI chat in the
 * panel and its canvas takes the page's place). Kept, off, to compare the two later: while it is off the switch does
 * not render and nobody's home is `/ai`, whatever a user remembered. `/ai` itself still opens by its URL.
 */
let aiModeSwitchEnabled = false

export function isAiModeSwitchEnabled(): boolean {
  return aiModeSwitchEnabled
}

/** Shows the switch again (and lets the remembered mode be the home) — tests, or a later UX comparison. */
export function setAiModeSwitchEnabled(on: boolean): void {
  aiModeSwitchEnabled = on
}

export interface HomeOptions {
  /**
   * AI mode is the user's home. Read when absent: the preference the navbar switch remembers for this user (auth store)
   * with the beta flag on — `false` before the store and the configuration exist (unit tests of the plain rule).
   */
  aiMode?: boolean
}

/** The user turned AI mode on with the navbar switch (remembered per user) and the beta flag is on. */
export function aiModeIsHome(session: Session): boolean {
  if (!aiModeSwitchEnabled || !getActivePinia()) return false
  try {
    if (!getConfig().betaFeature) return false
  } catch {
    return false
  }
  return useAuthStore().isAiMode(session)
}

/**
 * The home rule (legacy `auth/utils.js:26-39`, wp/shell §4.1), computed at navigation time (legacy computed `/`'s
 * target once, at load): no session → login; AI mode remembered for the user (beta flag on) → `/ai`
 * (docs/ai-mode/wp/AI-1.md); a Freelancer without a workspace → `/ca/resume`; otherwise the current software's
 * dashboard. One helper for `/`, after login, after a software switch and on the error pages' "Back to home"
 * (KIT_REQUIREMENTS.md §2).
 *
 * A session without a current software (legacy sent it to `login`, which bounces a signed-in user straight back —
 * NEW-11), or whose stored software is not among its softwares any more (a session stored before Onboarding and
 * Engagement joined Human resources, BR-097), falls back to the first of the user's softwares, else the profile page.
 */
export function getHomeRoute(session: Session | null, options: HomeOptions = {}): RouteLocationRaw {
  if (!session) return { name: ROUTE_LOGIN }
  if (options.aiMode ?? aiModeIsHome(session)) return { path: AI_MODE_PATH }
  if (session.userType === 'Freelancer' && !session.currentWorkspace) return { path: '/ca/resume' }
  const stored = session.currentSoftware
  const current = stored && session.softwares.some((entry) => entry.short === stored) ? stored : null
  const software = current ?? session.softwares[0]?.short ?? null
  if (!software) return { name: ROUTE_PROFILE }
  return { path: `/${software}/dashboard` }
}
